/**
 * Invoice Create/Edit Page (T088)
 * Create new invoices or edit existing DRAFT invoices
 */

import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useToast } from '../components/ui';
import InvoiceForm, { type InvoiceFormData } from '../components/Invoices/InvoiceForm';
import { useInvoice, useCreateInvoice, useUpdateInvoice } from '../hooks/useInvoices';

export default function InvoiceCreate() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isEditing = !!id;

  // Fetch existing invoice for edit mode
  const { data: invoice, isLoading } = useInvoice(id || '');

  const createInvoice = useCreateInvoice();
  const updateInvoice = useUpdateInvoice();

  const handleSubmit = async (data: InvoiceFormData) => {
    try {
      if (isEditing && id) {
        await updateInvoice.mutateAsync({ id, data });
        showToast('success', 'Invoice updated');
      } else {
        await createInvoice.mutateAsync(data);
        showToast('success', 'Invoice created');
      }
      navigate('/invoices');
    } catch {
      showToast('error', `Failed to ${isEditing ? 'update' : 'create'} invoice`);
    }
  };

  const handleSaveDraft = async (data: InvoiceFormData) => {
    try {
      if (isEditing && id) {
        await updateInvoice.mutateAsync({ id, data });
        showToast('success', 'Draft updated');
      } else {
        await createInvoice.mutateAsync(data);
        showToast('success', 'Draft saved');
      }
      navigate('/invoices');
    } catch {
      showToast('error', 'Failed to save draft');
    }
  };

  if (isEditing && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <div className="claude-spinner mx-auto"></div>
          <p className="text-muted-foreground">Loading invoice...</p>
        </div>
      </div>
    );
  }

  // Prevent editing non-DRAFT invoices
  if (isEditing && invoice && invoice.status !== 'DRAFT') {
    return (
      <div className="space-y-6 pb-8">
        <button onClick={() => navigate('/invoices')} className="claude-button claude-button-ghost">
          <ArrowLeft className="w-4 h-4" />
          Back to Invoices
        </button>
        <div className="claude-card p-8 text-center space-y-3">
          <h2 className="text-xl font-semibold">Cannot Edit Invoice</h2>
          <p className="text-muted-foreground">
            Only DRAFT invoices can be edited. This invoice has status: {invoice.status}
          </p>
        </div>
      </div>
    );
  }

  // Build initial data for edit mode
  const initialData =
    isEditing && invoice
      ? {
          customerId: invoice.customerId,
          customerName: invoice.customerName,
          customerEmail: invoice.customerEmail,
          customerPhone: invoice.customerPhone,
          dueDate: invoice.dueDate?.split('T')[0] || '',
          lineItems: invoice.lineItems.map((li) => ({
            description: li.description,
            quantity: li.quantity,
            unitPrice: li.unitPrice,
          })),
          notes: invoice.notes || '',
        }
      : undefined;

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/invoices')}
          className="claude-button claude-button-ghost p-2"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="space-y-1">
          <h1>{isEditing ? 'Edit Invoice' : 'Create Invoice'}</h1>
          <p className="text-muted-foreground">
            {isEditing
              ? `Editing invoice ${invoice?.invoiceNumber || ''}`
              : 'Create a new invoice for a customer'}
          </p>
        </div>
      </div>

      {/* Form */}
      <InvoiceForm
        initialData={initialData}
        onSubmit={handleSubmit}
        onSaveDraft={handleSaveDraft}
        isSubmitting={createInvoice.isPending || updateInvoice.isPending}
        submitLabel={isEditing ? 'Update Invoice' : 'Create Invoice'}
      />
    </div>
  );
}
